<template>
	<app-panel
		:title="update ? 'Update Existing List' : 'Insert New List'"
		:loading="loading"
		loading-overlay>
		<div v-if="list && list.name && step > 1" class="max-w-2xl mx-auto mb-4">
			<div
				class="flex items-center text-sm text-gray-600 gap-3 border-[1px] border-solid border-gray-200 rounded-lg px-4 py-[10px]"
				data-testid="current-list-name">
				<app-icon :icon="['fas', 'fa-check-circle']" class="h-4 text-green-500" />
				<strong v-if="update" class="shrink-0">Selected list:</strong>
				<strong v-else class="shrink-0">New list:</strong>

				{{ list.name }}

				<span v-if="update" class="ml-auto">{{
					formatDate(String(list.date_created))
				}}</span>
			</div>
		</div>

		<div
			v-if="list && list.list_file"
			class="max-w-2xl mx-auto mb-4"
			data-testid="current-list-file">
			<p class="font-medium text-sm text-gray-600 mb-1">Selected file:</p>
			<div
				class="flex items-center text-sm text-gray-600 gap-3 border-[1px] border-solid border-gray-200 rounded-lg px-4 py-[8px]">
				<app-icon :icon="['fas', 'fa-file-arrow-up']" class="h-4 text-blue-500" />
				{{ list.list_file[0].name }}

				<app-badge theme="outline">
					{{ fileSizeToHumanReadable(list.list_file[0].size) }}
				</app-badge>

				<button
					class="text-xs font-medium text-red-600 opacity-60 hover:opacity-100 ml-auto"
					@click="removeFile()">
					Remove
				</button>
			</div>
		</div>

		<!-- STEP 1 -->
		<div v-if="step === 1" data-testid="step-1">
			<!-- UPDATE -->
			<div v-if="update" class="max-w-2xl mx-auto">
				<div
					v-if="list && list.name"
					class="flex items-center gap-3"
					data-testid="selected-list">
					<app-alert spacing="mb-0">
						<strong class="shrink-0">Selected list:</strong>
						<span class="flex w-full items-center justify-between">
							{{ list.name }}
							<span class="ml-auto">{{ formatDate(String(list.date_created)) }}</span>
						</span>
					</app-alert>

					<button
						class="text-xs font-medium text-red-600 opacity-60 hover:opacity-100"
						@click="list = null">
						Change
					</button>
				</div>

				<datatable
					v-else
					:headers
					:value="lists"
					data-testid="list-table"
					class="cursor-pointer"
					@row-click="(item: MailList) => (list = item)">
					<template #row-date_created="{ row }">
						{{ formatDate(row.date_created) }}
					</template>
				</datatable>
			</div>

			<!-- INSERT -->
			<div v-else class="max-w-2xl mx-auto">
				<app-input
					v-model="list.name"
					label="Enter a name for your list"
					placeholder="Example List Name"
					:error="list.name && !isValid ? 'List name is already in use' : undefined"
					inline
					data-testid="new-list-input" />
			</div>

			<div class="flex justify-end mt-5">
				<app-button :disabled="!isValid" data-testid="continue-button" @click="step++">
					Continue
				</app-button>
			</div>
		</div>

		<!-- STEP 2 -->
		<div v-if="step === 2" data-testid="step-2">
			<div v-if="!list.list_file" class="max-w-2xl mx-auto text-center">
				<p class="font-medium text-mabey-950">Upload a list using the button below</p>

				<div class="flex justify-center mb-4 mt-5">
					<app-file-button
						name="list_file"
						:value="list.list_file"
						accept=".csv"
						icon="upload"
						size="large"
						@file-change="(file: File) => (list.list_file = file)">
						Browse Files
					</app-file-button>
				</div>

				<span class="text-gray-500 text-xs"> CSV file types only </span>
			</div>

			<div class="flex justify-end mt-5">
				<app-button
					v-if="update"
					:disabled="!isValid"
					data-testid="confirm-button"
					@click="submit">
					Confirm and upload
				</app-button>

				<app-button
					v-else
					:disabled="!isValid"
					data-testid="continue-button"
					@click="step++">
					Continue
				</app-button>
			</div>
		</div>

		<!-- STEP 3 -->
		<div v-if="step === 3" data-testid="step-3">
			<div class="max-w-2xl mx-auto">
				<app-input
					v-model="list.default_name"
					placeholder="Sender name"
					label="Mail sender name"
					class="mb-4"
					:error="errors?.default_name?.[0]" />

				<app-input
					v-model="list.default_email"
					placeholder="Sender email"
					label="Default email"
					class="mb-4"
					:error="errors?.default_email?.[0]" />

				<app-input
					v-model="list.default_subject"
					placeholder="Subject"
					label="Default subject line"
					type="textarea"
					class="mb-4"
					:error="errors?.default_subject?.[0]" />

				<app-input
					v-model="list.list_permission_text"
					placeholder="Permission text"
					label="Permission text"
					type="textarea"
					class="mb-4"
					:error="errors?.list_permission_text?.[0]" />
			</div>

			<div class="flex justify-end mt-5">
				<app-button :disabled="!isValid" data-testid="confirm-button" @click="submit">
					Confirm and upload
				</app-button>
			</div>
		</div>
	</app-panel>
</template>

<script lang="ts" setup>
	import { computed, ref } from 'vue'
	import type { MailList } from '@/data/types/mail-list'
	import { formatDate } from '@/utils/date'
	import { fileSizeToHumanReadable } from '@/utils/fileSize'
	import AppInput from '@/components/base/forms/AppInput.vue'
	import MailchimpService from '@/services/mailchimp'
	import useToaster from '@/stores/toaster'
	import type { Header } from '@/data/types/datatable'

	const api = new MailchimpService()
	const toaster = useToaster()

	const props = defineProps<{
		update?: boolean
		lists: MailList[]
	}>()

	const step = ref(1)
	const list = ref<MailList>({
		list_file: null,
		name: null,
		list_type: null
	})
	const errors = ref()
	const loading = ref(false)

	const headers: Header[] = [
		{ value: 'id', text: 'List ID', props: { sortable: true } },
		{ value: 'name', text: 'List Name', props: { sortable: true } },
		{ value: 'date_created', text: 'List Created', props: { sortable: true } }
	]

	const isValid = computed(() => {
		if (step.value == 1) {
			if (!list.value?.name) {
				return false
			}

			if (!props.update) {
				return !props.lists.some((item) => item.name === list.value.name)
			}

			return true
		}

		if (step.value == 2) {
			return list.value.list_file
		}

		return true
	})

	function removeFile() {
		step.value = 2
		list.value.list_file = undefined
	}

	function submit() {
		loading.value = true

		toaster.working({
			text: 'Uploading mail list...'
		})

		api.create({
			list_id: props.update ? list.value.id : undefined,
			list_type: props.update ? 'UPSERT' : 'INSERT',
			list_name: list.value.name,
			list_file: list.value.list_file[0],
			default_name: list.value.default_name ?? undefined,
			default_email: list.value.default_email ?? undefined,
			default_subject: list.value.default_subject ?? undefined,
			list_permission_text: list.value.list_permission_text ?? undefined
		})
			.then((response) => {
				emit('uploaded', {
					name: list.value.name,
					...response.data
				})
				toaster.clear()
				toaster.success({
					text: 'Mail list uploaded successfully'
				})
			})
			.catch((error) => {
				errors.value = error.response.data
				toaster.clear()
				console.error(error)
				toaster.error({
					text: 'Failed to upload mail list'
				})
			})
			.finally(() => {
				loading.value = false
			})
	}

	const emit = defineEmits(['uploaded'])
</script>
